import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCart, removeCartItem } from "../../redux/features/cartSlice"; // Import fetchCart action
import { Link, useNavigate } from "react-router-dom";
import { updateCart } from "../../redux/features/cartSlice";
import { validateCouponAsync, clearCoupon } from "../../redux/features/couponSlice";
import { useTranslation } from "react-i18next";

function Cart() {
  const { t } = useTranslation()
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    cartItems,
    totalAmount,
    deliveryCharges,
    netAmount,
    couponDiscount,
    loading,
    error,
  } = useSelector((state) => state.cart);
  const [coupon, setCoupon] = useState("");
  const [updatingIds, setUpdatingIds] = useState({}); // product_id => boolean

  const lang = useSelector((state) => state.language.lang);
  const { isValid, discount, status, code } = useSelector(
    (state) => state.coupon
  );

  const handleValidateCoupon = async () => {
    if (coupon.trim()) {
      const result = await dispatch(validateCouponAsync(coupon));

      if (validateCouponAsync.fulfilled.match(result)) {
        dispatch(fetchCart(coupon)); // only if coupon valid
      }
    }
  };

  const handleClearCoupon = () => {
    setCoupon("");
    dispatch(clearCoupon());
    dispatch(fetchCart({ lang }));
  };

  useEffect(() => {
    setTimeout(() => {
      dispatch(fetchCart({ lang })); // Fetch cart data on first load
    }, 300)
  }, [dispatch, lang]);

  const handleRemoveItem = (product_id) => {
    setUpdatingIds((prev) => ({ ...prev, [product_id]: true }));

    dispatch(removeCartItem(product_id))
      .then(() => dispatch(fetchCart({ lang })))
      .catch((err) => console.error("Error removing item:", err))
      .finally(() =>
        setUpdatingIds((prev) => ({ ...prev, [product_id]: false }))
      );
  };


  const handleUpdateCart = (product_id, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdatingIds((prev) => ({ ...prev, [product_id]: true }));
    dispatch(updateCart({ product_id, quantity: newQuantity })).finally(() =>
      setUpdatingIds((prev) => ({ ...prev, [product_id]: false }))
    );
  };

  // Check if there are no items in the cart
  if (cartItems.length === 0) {
    return (
      <section className="cart-page-section py-5 main-bg">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8 text-center">
              <div className="bg-white rounded-4 p-4 shadow-sm">
                <h3>{t(`Your_cart_is_empty`)}</h3>
                <p className="text-muted">{t(`Cart_empty_message`)}</p>
                <div className="ct-shopping d-flex justify-content-center mt-2">
                  <Link to="/shops" className="theme-btn style-one d-inline-block">
                    {t(`Continue_Shopping`)}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cart-page-section pt-5 pb-80 main-bg">
      <div className="container">
        <div className="row">
          {/* Left Side - Cart Items */}
          <div className="col-xl-8">
            <div
              className="cart-wrapper mb-40"
            // data-aos="fade-up"
            // data-aos-duration="1200"
            >
              <h3 className="mb-20">{t(`Total_Cart_Items`)}: {cartItems.length}</h3>

              <div className="d-flex flex-column gap-3 mb-3">
                {cartItems.length > 0 ? (
                  cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="cart-card p-3 rounded-4 position-relative"
                      style={{
                        background: "#fff",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.05)";
                      }}
                    >
                      <div className="d-flex gap-3 align-items-start">
                        {/* Product Image */}
                        <div
                          className="flex-shrink-0 rounded-3 overflow-hidden bg-light"
                          style={{
                            width: 110,
                            height: 110,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Link to={`/product/${item.product_id}`} className="d-block w-100 h-100">
                            <img
                              src={item.image}
                              alt="No Image"
                              onError={(e) => {
                                e.target.onerror = null; // prevents infinite loop
                                e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                              }}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                transition: "transform 0.3s ease",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                            />
                          </Link>
                        </div>

                        {/* Product Info */}
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="fw-semibold mb-1" style={{ color: "#003d4d" }}>
                              <Link
                                to={`/product/${item.product_id}`}
                                className="text-decoration-none"
                                style={{
                                  color: "inherit",
                                  transition: "color 0.2s ease",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--secondary-color)")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "#003d4d")}
                              >
                                {item.title}
                              </Link>
                            </h5>
                            <button
                              className="btn btn-link text-danger p-0 ms-3"
                              onClick={() => handleRemoveItem(item.id)}
                              aria-label={`Remove ${item.title} from cart`}
                              disabled={!!updatingIds[item.product_id]}
                              style={{ fontSize: "1.1rem" }}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>

                          {/* Variations */}
                          {item.combinations?.filter((c) => c.key !== "1").length > 0 && (
                            <div className="small text-muted mb-2">
                              {item.combinations
                                .filter((combo) => combo.key !== "1")
                                .map((combo, index) => (
                                  <span
                                    key={index}
                                    className="badge bg-light text-dark me-2 fw-normal border rounded-pill"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {combo.key}: {combo.value}
                                  </span>
                                ))}
                            </div>
                          )}

                          {/* Quantity, Price, and Total */}
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                            <div
                              className="d-flex align-items-center border rounded-pill"
                              style={{
                                borderColor: "rgba(0,0,0,0.1)",
                                background: "#f8f9fa",
                                padding: "4px 8px",
                              }}
                            >
                              <button
                                className="btn btn-sm p-1"
                                disabled={!!updatingIds[item.product_id]}
                                onClick={() => handleUpdateCart(item.product_id, item.quantity - 1)}
                              >
                                <i className="fa fa-minus"></i>
                              </button>
                              <div className="px-2 fw-bold">{item.quantity}</div>
                              <button
                                className="btn btn-sm p-1"
                                disabled={!!updatingIds[item.product_id]}
                                onClick={() => handleUpdateCart(item.product_id, item.quantity + 1)}
                              >
                                <i className="fa fa-plus"></i>
                              </button>
                            </div>

                            <div className="text-end small flex-grow-1">
                              <div className="text-muted">{t(`Price`)}</div>
                              <div className="fw-semibold">€{item?.sales_price?.toFixed(2)}</div>
                            </div>

                            <div className="text-end" style={{ minWidth: 120 }}>
                              <div className="text-muted">{t(`Total`)}</div>
                              <div className="fw-bold text-success">
                                €{(item?.quantity * item?.sales_price).toFixed(2)}
                              </div>
                            </div>
                          </div>

                          {/* Delivery Date */}
                          <div className="mt-3 small text-muted">
                            <i className="fa fa-truck me-1 text-success"></i>
                            <b>Estimated Delivery:</b>{" "}
                            {new Date(Date.now() + (item?.delivered_day || 2) * 86400000).toLocaleDateString(
                              "en-US",
                              { weekday: "long", day: "2-digit", month: "long", year: "numeric" }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">{t(`Your_cart_is_empty`)}</p>
                  </div>
                )}
              </div>

              <div className="cart-bottom d-flex align-items-center justify-content-between mt-40 mt-4 pt-2 border-top" style={{ marginTop: 24 }}>
                <div className="ct-shopping">
                  <button
                    className="theme-btn style-one"
                    onClick={() => Navigate(`/shops`)}
                  >
                    {t(`Continue_Shopping`)}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Cart Summary */}
          <div className="col-xl-4">
            <div className="cart-sidebar-area" style={{ position: "sticky", top: 96 }}>
              <div
                className="cart-widget coupon-box-widget mb-40"
              >
                <h4>{t(`Use_Coupon_Code`)}</h4>
                <p>{t(`Enter_your_coupon_code`)}</p>
                <div className="input-group shadow-sm rounded-3 overflow-hidden">
                  <input
                    type="text"
                    className="form-control border-0"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                    placeholder={t(`Enter_coupon`)}
                  />
                  {isValid ? (
                    <button
                      className="btn btn-warning"
                      onClick={handleClearCoupon}
                    >
                      {t(`Remove`)}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={handleValidateCoupon}
                      disabled={status === "loading"}
                    >
                      {status === "loading" ? `${t("Validating")}` : `${t('Apply')}`}
                    </button>
                  )}
                </div>

                {/* ✅ Message below input */}
                {status === "failed" && (
                  <p className="text-danger mt-2 small mb-0">{error || `${t("Invalid_coupon_code")}`}</p>
                )}
                {status === "succeeded" && isValid && (
                  <p className="text-success mt-2 small">
                    {t(`Coupon_applied_success`)} ₹{discount}.
                  </p>
                )}
              </div>
              <div
                className="cart-widget cart-total-widget mb-40 bg-white rounded-4 shadow-sm p-3"
              // data-aos="fade-up"
              // data-aos-duration="1400"
              >
                <h4 className="mb-3">{`${t('Order_Summary')}`}</h4>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="text-muted">{`${t('Subtotal')}`}</span>
                  <span className="fw-semibold">€{totalAmount?.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="text-muted">{`${t('Shipping')}`}</span>
                  <span className="fw-semibold">€{deliveryCharges?.toFixed(2)}</span>
                </div>
                {couponDiscount != 0 ? (
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{`${t('Coupon_Discount')}`}</span>
                    <span className="fw-semibold">-€{couponDiscount?.toFixed(2)}</span>
                  </div>
                ) : null}
                <div className="d-flex justify-content-between py-3">
                  <span className="fw-bold">{t(`Total`)}</span>
                  <span className="fw-bold">€{netAmount?.toFixed(2)}</span>
                </div>
                <div className="proceed-checkout">
                  <Link to="/checkout" className="btn btn-primary w-100 py-2">
                    {`${t('Proceed_to_checkout')}`}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile fixed checkout bar */}
      <div className="d-xl-none d-block fixed-bottom bg-white border-top shadow-sm">
        <div className="container py-2 d-flex align-items-center justify-content-between">
          <div>
            <div className="small text-muted">{t(`Total`)}</div>
            <div className="fw-bold">€{netAmount?.toFixed(2)}</div>
          </div>
          <Link to="/checkout" className="theme-btn style-one">
            {`${t('Checkout')}`}
          </Link>
        </div>
      </div>
    </section>
  );
}

export default Cart;
